import { useEffect, useState } from "react";
import { apiClient, ApiError } from "../../lib/api-client";
import { formatDateSaoPaulo } from "../../lib/currency";

interface AdminCollaborator {
  id: string;
  tenantId: string;
  tenantTradeName: string;
  tenantSlug: string;
  userId: string;
  externalRef: string | null;
  status: "active" | "suspended" | "removed";
  retentionUntil: string | null;
  createdAt: string;
  titlesConsumed: number;
}

export function AdminCollaboratorsPage() {
  const [collaborators, setCollaborators] = useState<AdminCollaborator[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  function loadCollaborators() {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set("status", statusFilter);

    apiClient
      .get<{ items: AdminCollaborator[] }>(`/admin/collaborators?${params.toString()}`)
      .then((data) => setCollaborators(data.items))
      .catch((err) => setError(err instanceof ApiError ? err.message : "Não foi possível carregar colaboradores."))
      .finally(() => setLoading(false));
  }

  useEffect(loadCollaborators, [statusFilter]);

  async function handleToggleStatus(id: string, currentStatus: string) {
    const newStatus = currentStatus === "active" ? "suspended" : "active";
    setError(null);
    setMessage(null);
    try {
      await apiClient.patch("/admin/collaborators", { id, status: newStatus });
      setMessage(`Status do colaborador alterado para "${newStatus}".`);
      loadCollaborators();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao alterar status.");
    }
  }

  return (
    <div>
      <h2>Colaboradores</h2>

      {message && <p role="status" className="notice-success">{message}</p>}
      {error && <p role="alert" className="notice-error">{error}</p>}

      <div className="card" style={{ marginBottom: "var(--space-4)", display: "flex", gap: "var(--space-4)", alignItems: "center" }}>
        <label htmlFor="statusFilterSelect"><strong>Filtrar por Status:</strong></label>
        <select
          id="statusFilterSelect"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ width: "auto" }}
        >
          <option value="">Todos</option>
          <option value="active">Ativos</option>
          <option value="suspended">Suspensos</option>
          <option value="removed">Removidos</option>
        </select>
      </div>

      <div className="card" style={{ overflowX: "auto" }}>
        {loading ? (
          <p className="text-muted">Carregando colaboradores...</p>
        ) : collaborators.length === 0 ? (
          <p className="text-muted">Nenhum colaborador encontrado com os filtros selecionados.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Empresa</th>
                <th>Matrícula / Ref</th>
                <th>Status</th>
                <th className="num">Títulos Acessados</th>
                <th>Retenção LGPD</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {collaborators.map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.tenantTradeName}</strong>
                    <div className="text-muted" style={{ fontSize: "var(--text-xs)" }}>
                      /{c.tenantSlug}
                    </div>
                  </td>
                  <td>{c.externalRef ?? "(não informado)"}</td>
                  <td>
                    <span
                      className={`status-badge ${
                        c.status === "active"
                          ? "status-badge--paid"
                          : c.status === "suspended"
                          ? "status-badge--pending"
                          : "status-badge--negative"
                      }`}
                    >
                      {c.status}
                    </span>
                  </td>
                  <td className="num">{c.titlesConsumed}</td>
                  <td>{c.retentionUntil ? formatDateSaoPaulo(c.retentionUntil) : "Padrão (36m)"}</td>
                  <td>
                    <button
                      className="btn-secondary"
                      style={{ fontSize: "var(--text-xs)", padding: "var(--space-1) var(--space-2)" }}
                      onClick={() => handleToggleStatus(c.id, c.status)}
                    >
                      {c.status === "active" ? "Suspender" : "Ativar"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
